// Sample numbers for the Blunders and Misses cards: the shape their
// skeletons take while the store loads, and the stories' data. Six motif
// families have a mistake, so the patterns card shows its five rows and
// the note that one family is left out.
import type { MoveKind } from '@chess/api-model';
import type { MistakeKey } from '@chess/api-mistakes';
import type { GroupStat, MistakeGroups } from './MistakeGroupsAggregator.ts';

const stat = (games: number, mistakes: number, cost: number): GroupStat => ({ games, mistakes, cost });

export function sampleGroups(kind: MoveKind): MistakeGroups {
  const g = (dimension: string, value: string | number, s: GroupStat): [MistakeKey, GroupStat] => [`${kind}-${dimension}-${value}` as MistakeKey, s];
  return {
    kind,
    scanned: 40,
    games: 27,
    mistakes: 61,
    noClock: 3,
    groups: Object.fromEntries([
      g('motif', 'hangingPiece', stat(14, 19, 41.5)),
      g('motif', 'hangingPiece/minor', stat(9, 12, 26.1)),
      g('motif', 'hangingPiece/rook', stat(5, 7, 15.4)),
      g('motif', 'fork', stat(6, 7, 19.8)),
      g('motif', 'fork/knight', stat(5, 6, 17.3)),
      g('motif', 'fork/pawn', stat(1, 1, 2.5)),
      g('motif', 'mate', stat(4, 4, 18.2)),
      g('motif', 'mate/backRank', stat(3, 3, 14.2)),
      g('motif', 'mate/other', stat(1, 1, 4)),
      g('motif', 'pin', stat(4, 5, 13.9)),
      g('motif', 'pin/queen', stat(3, 4, 11.4)),
      g('motif', 'pin/king', stat(1, 1, 2.5)),
      g('motif', 'discoveredAttack', stat(3, 3, 8.5)),
      g('motif', 'discoveredAttack/check', stat(3, 3, 8.5)),
      g('motif', 'positional', stat(15, 20, 12.6)),
      g('severity', 'decisive', stat(17, 24, 70.1)),
      g('severity', 'serious', stat(12, 16, 27.3)),
      g('severity', 'minor', stat(13, 21, 17.1)),
      g('standing', 'winning', stat(9, 13, 21.4)),
      g('standing', 'level', stat(19, 33, 70.3)),
      g('standing', 'losing', stat(8, 15, 22.8)),
      g('clock', 'under1', stat(7, 12, 30.2)),
      g('clock', '1to3', stat(10, 18, 34.9)),
      g('clock', '3plus', stat(15, 25, 41.0)),
      g('material', 'queen', stat(3, 3, 9)),
      g('material', 'rook', stat(5, 5, 13.1)),
      g('material', 'bishop', stat(6, 7, 15.5)),
      g('material', 'knight', stat(7, 8, 17.2)),
      g('material', 'pawn', stat(9, 14, 11.8)),
      g('material', 'mate', stat(4, 4, 18.2)),
      g('material', 'none', stat(15, 20, 12.6)),
      g('week', 0, stat(6, 14, 20)),
      g('week', 1, stat(7, 18, 30)),
      g('week', 2, stat(8, 17, 34)),
      g('week', 3, stat(6, 12, 30)),
    ]),
    weeks: [
      { week: 0, mistakes: 14, scanned: 9 },
      { week: 1, mistakes: 18, scanned: 10 },
      { week: 2, mistakes: 17, scanned: 12 },
      { week: 3, mistakes: 12, scanned: 9 },
    ],
  };
}

/** The sample with a mistake in every motif family: the patterns card's cut at its widest. */
export function crowdedGroups(kind: MoveKind): MistakeGroups {
  const base = sampleGroups(kind);
  const more: Array<[string, GroupStat]> = [
    ['skewer', stat(2, 2, 6.1)],
    ['trappedPiece', stat(2, 2, 5.3)],
    ['captureDefender', stat(1, 1, 3.2)],
    ['pawn', stat(3, 3, 4.4)],
    ['kingAttack', stat(2, 3, 7.9)],
    ['combination', stat(1, 1, 3.6)],
  ];
  return {
    ...base,
    mistakes: base.mistakes + more.reduce((n, [, s]) => n + s.mistakes, 0),
    groups: { ...base.groups, ...Object.fromEntries(more.map(([f, s]) => [`${kind}-motif-${f}` as MistakeKey, s])) },
  };
}

/** No mistakes of the kind in scanned games. */
export const cleanGroups = (kind: MoveKind): MistakeGroups => ({
  kind,
  scanned: 12,
  games: 0,
  mistakes: 0,
  noClock: 0,
  groups: {},
  weeks: [0, 1, 2, 3].map((week) => ({ week: week as 0 | 1 | 2 | 3, mistakes: 0, scanned: 3 })),
});
